import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Layout } from 'antd';
import { Outlet, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import Header from '@/shared/components/ui/organisms/Header';
import { PortalsGrid } from '@/features/home-v2/components/ui/organisms';
import { NavigationOverlay } from '@/shared/components/ui/atoms';
import logoAsset from '@/assets/images/logo.png';
import type { HomeLayoutProps } from './types';
import { useAuthStore } from '@/features/auth/store/auth';
import { useWorkspaceStore } from '@/shared/store/workspace';
import { useUserPermissions } from '@/shared/hooks/useUserPermissions';
import { useUserInfo } from '@/features/auth/hooks/useUserInfo';
import { useUserGuiding } from '@/shared/hooks/useUserGuiding';
import './HomeLayout.css';

/**
 * HomeLayout
 *
 * Composes `Header` to provide the unified home portal shell. The header
 * displays logo (clickable to home), user info, profile options and the
 * "Abrir aplicações" button, which opens the `PortalsGrid` as the
 * cross-portal entry point.
 *
 * ## Features
 * - **Portals grid**: Header apps launcher to the PDT / Portal ID homes
 * - **Sticky header**: Logo (clickable), user name, profile menu, and email
 *   from `email` with `alternativeEmails` fallback for federated accounts
 * - **Logo navigation**: Click logo to return to unified home
 * - **Responsive**: Adjusts content padding on smaller screens
 * - **Accessible landmarks**: nav + header + main regions
 *
 * ## Layout Flow
 * ```mermaid
 * flowchart LR
 *   H[Header with Logo] --> MC[Main Content]
 * ```
 *
 * @example
 * ```tsx
 * <HomeLayout>
 *   <Dashboard />
 * </HomeLayout>
 * ```
 */
export const HomeLayout: React.FC<HomeLayoutProps> = ({
  className = '',
  style,
  'aria-label': ariaLabel,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const resetAuth = useAuthStore((s) => s.reset);
  const resetWorkspace = useWorkspaceStore((s) => s.reset);
  const { canManageUsers } = useUserPermissions();
  const { data: userInfo } = useUserInfo();
  useUserGuiding();

  const appsMenuContent = useCallback(
    (close: () => void) => <PortalsGrid onNavigate={close} />,
    []
  );

  const primaryEmail = userInfo?.email
    ? typeof userInfo.email === 'string'
      ? userInfo.email
      : userInfo.email.email
    : undefined;
  const userEmail =
    primaryEmail ||
    (userInfo?.alternativeEmails?.find((email) => email.main)?.email ??
      userInfo?.alternativeEmails?.[0]?.email);
  const companyName = userInfo?.businessAccount?.name;

  /**
   * Handle logout - reset auth state, clear query cache, and redirect to login
   */
  const handleLogout = useCallback(() => {
    queryClient.clear();
    resetWorkspace();
    resetAuth();
    navigate('/login', { replace: true });
  }, [queryClient, resetAuth, resetWorkspace, navigate]);

  /**
   * Handle logo click - navigate to home
   */
  const handleLogoClick = () => {
    navigate('/');
  };

  return (
    <Layout
      className={`home-layout ${className}`.replace(/\s+/g, ' ').trim()}
      style={style}
      aria-label={ariaLabel || t('Home portal layout')}
    >
      <Layout className="home-layout__content-wrapper">
        <div
          className="home-layout__header-wrapper"
          aria-label={t('Global header')}
        >
          <Header
            logoSrc={logoAsset}
            logoNode={
              <button
                onClick={handleLogoClick}
                className="home-layout__logo-button"
                aria-label={t('Go to unified home')}
                type="button"
              >
                <img src={logoAsset} alt="Serasa Logo" style={{ height: 40 }} />
              </button>
            }
            appsMenuContent={appsMenuContent}
            contactEmail={userEmail}
            entityName={companyName}
            showAppsIcon
            onLogout={handleLogout}
            canManageUsers={canManageUsers}
          />
        </div>
        <main
          className="home-layout__main"
          aria-label={t('Page content')}
          role="main"
        >
          <Outlet />
        </main>
      </Layout>
      <NavigationOverlay />
    </Layout>
  );
};

export default HomeLayout;
